import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { Pill } from '../components/Pill';
import { fmtDollars, fmtNum, fmtRelative } from '../lib/format';
import type { ActiveCollectionRow, TopCardRow } from '../api/types';

export function CollectionsScreen() {
  const navigate = useNavigate();
  const { data, isFetching } = useQuery({
    queryKey: ['top-cards', 100],
    queryFn: () => insightsApi.topCards(100),
  });
  const active = useQuery({
    queryKey: ['active-collections', 50],
    queryFn: () => insightsApi.activeCollections(50),
  });
  const rows = data ?? [];

  const activeColumns: Column<ActiveCollectionRow>[] = [
    {
      key: 'name',
      header: 'Collection',
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>
            {r.name}{r.isDefault ? '' : ''}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
            user #{r.userId}{r.userCountry ? ` · ${r.userCountry}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'cardCount',
      header: 'Cards',
      sortKey: 'cardCount',
      align: 'right',
      render: (r) => <Pill tone="brand" size="sm">{fmtNum(r.cardCount)}</Pill>,
    },
    {
      key: 'totalValue',
      header: 'Value',
      sortKey: (r) => parseFloat(r.totalValue),
      align: 'right',
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtDollars(r.totalValue)}</span>,
    },
    {
      key: 'lastActivity',
      header: 'Last activity',
      sortKey: (r) => (r.lastActivity ? new Date(r.lastActivity).getTime() : 0),
      align: 'right',
      hideMobile: true,
      render: (r) => (
        <span style={{ color: 'var(--ink-3)' }}>{r.lastActivity ? fmtRelative(r.lastActivity) : '—'}</span>
      ),
    },
  ];

  const columns: Column<TopCardRow>[] = [
    {
      key: 'card',
      header: 'Card',
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {r.imageUrl ? (
            <img
              src={r.imageUrl}
              alt=""
              style={{ width: 30, height: 42, objectFit: 'cover', borderRadius: 4, background: 'var(--bg-sunken)' }}
            />
          ) : (
            <div style={{ width: 30, height: 42, borderRadius: 4, background: 'var(--bg-sunken)' }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{r.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {r.setName ?? '—'}{r.number ? ` · #${r.number}` : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'totalQuantity',
      header: 'Copies held',
      sortKey: 'totalQuantity',
      align: 'right',
      render: (r) => <Pill tone="brand" size="sm">{fmtNum(r.totalQuantity)}</Pill>,
    },
    {
      key: 'distinctUsers',
      header: 'Collectors',
      sortKey: 'distinctUsers',
      align: 'right',
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtNum(r.distinctUsers)}</span>,
    },
    {
      key: 'lineCount',
      header: 'Listings',
      sortKey: 'lineCount',
      align: 'right',
      hideMobile: true,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-3)' }}>{fmtNum(r.lineCount)}</span>,
    },
  ];

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
            Most active collections
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Real users' biggest collections · click a row to open the owner
          </div>
        </div>
        <DataTable
          rows={active.data ?? []}
          columns={activeColumns}
          rowKey={(r) => r.collectionId}
          loading={active.isFetching}
          pageSize={25}
          onRowClick={(r) => navigate(`/users/${r.userId}`)}
          emptyState="No collections yet."
        />
      </Card>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
            Most collected cards
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Across every user collection · top 100 by copies held
          </div>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.cardId}
          loading={isFetching}
          pageSize={25}
          emptyState="No collected cards yet."
        />
      </Card>
    </div>
  );
}
